import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Users, Palette, Building2, Ruler } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useCMSContent, getSectionByType } from '../hooks/useCMSContent';
import axios from 'axios';
import { brandTitle } from '../seo/seoCore';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping
const iconMap = { Users, Palette, Building2, Ruler };

const defaultBenefits = [
  { icon: Palette, title: 'Sample Support', description: 'Access to fabric samples and swatches for client presentations' },
  { icon: Users, title: 'Dedicated Support', description: 'Personal account manager for specifications and selections' },
  { icon: Ruler, title: 'Project Coordination', description: 'Measuring and installation coordination for multi-unit projects' },
  { icon: Building2, title: 'Volume Pricing', description: 'Competitive pricing for trade professionals and large projects' },
];

const businessTypes = [
  { id: 'interior-designer', name: 'Interior Designer' },
  { id: 'architect', name: 'Architect' },
  { id: 'builder-developer', name: 'Builder / Developer' },
  { id: 'contractor', name: 'Contractor' },
  { id: 'dealer-reseller', name: 'Dealer / Reseller' },
  { id: 'custom-av-integrator', name: 'Custom AV Integrator' },
];

const referralSources = [
  { id: 'referral', name: 'Referral / Word of Mouth' },
  { id: 'google', name: 'Google Search' },
  { id: 'social-media', name: 'Social Media' },
  { id: 'trade-show', name: 'Trade Show / Event' },
  { id: 'industry-publication', name: 'Industry Publication' },
  { id: 'other', name: 'Other' },
];

export const TradePage = () => {
  const { sections, seo } = useCMSContent('trade');
  const heroSection = getSectionByType(sections, 'hero');
  const benefitsSection = getSectionByType(sections, 'features');

  // Use CMS benefits or defaults
  const benefits = benefitsSection?.items?.length > 0 
    ? benefitsSection.items.map(item => ({ ...item, icon: iconMap[item.icon] || Palette }))
    : defaultBenefits;

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    business_type: '',
    projects_per_year: '',
    how_found_us: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (seo?.title) document.title = brandTitle(seo.title);
  }, [seo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/trade/apply`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero - CMS Driven */}
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">{heroSection?.subtitle || 'Trade Program'}</span>
            <h1 className="mt-4 text-neutral-900">{heroSection?.title || 'Partner With Apollo Shades'}</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              {heroSection?.content || 'Join our trade program for interior designers, architects, builders, and dealers. Access exclusive benefits, dedicated support, and competitive pricing.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits - CMS Driven */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="text-center mb-16">
            <span className="label-overline text-amber-700">{benefitsSection?.subtitle || 'Why Partner With Us'}</span>
            <h2 className="mt-4">{benefitsSection?.title || 'Trade Program Benefits'}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center bg-neutral-100">
                    <IconComponent className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{benefit.title}</h4>
                  <p className="text-neutral-600 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="section-padding bg-neutral-50">
        <div className="container-xl">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <span className="label-overline text-amber-700">Apply Now</span>
              <h2 className="mt-4 mb-6">Open a Trade Account</h2>
              <p className="text-neutral-600 mb-8">
                Complete the application below and our team will review and contact you within 2 business days.
              </p>
              <ul className="space-y-4">
                {['Priority project support', 'Exclusive trade pricing', 'Sample library access', 'Installation coordination', 'Dedicated account manager'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-amber-600" />
                    <span className="text-neutral-600">{item}</span>
                  </li>
                ))}
              </ul>

              {/* ST-120 Section */}
              <div className="mt-10 p-6 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-semibold text-lg mb-2">Tax Exempt Resellers</h4>
                <p className="text-neutral-600 text-sm mb-4">
                  Are you a registered reseller? Submit your NY State ST-120 Resale Certificate to establish tax-exempt purchasing with Apollo Shades.
                </p>
                <Button 
                  onClick={() => window.location.href = '/st120'} 
                  variant="outline"
                  className="border-amber-600 text-amber-700 hover:bg-amber-100"
                >
                  Complete ST-120 Form
                </Button>
              </div>
            </div>

            <div className="lg:col-span-3 bg-white p-8 lg:p-10 border border-neutral-200">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl mb-2">Application Received</h3>
                  <p className="text-neutral-600">Our team will review your application and contact you within 2 business days.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="trade-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Full Name *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-2" />
                    </div>
                    <div>
                      <Label className="label-overline">Company Name *</Label>
                      <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} required className="mt-2" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Email *</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-2" />
                    </div>
                    <div>
                      <Label className="label-overline">Phone *</Label>
                      <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="mt-2" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">Website</Label>
                    <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://" className="mt-2" />
                  </div>
                  <div>
                    <Label className="label-overline">Business Type *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {businessTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, business_type: type.id })}
                          className={`p-3 text-sm border transition-colors ${formData.business_type === type.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}
                        >
                          {type.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Projects Per Year</Label>
                      <select
                        value={formData.projects_per_year}
                        onChange={(e) => setFormData({ ...formData, projects_per_year: e.target.value })}
                        className="w-full mt-2 input-form"
                      >
                        <option value="">Select...</option>
                        <option value="1-5">1-5 projects</option>
                        <option value="6-15">6-15 projects</option>
                        <option value="16-30">16-30 projects</option>
                        <option value="30+">30+ projects</option>
                      </select>
                    </div>
                    <div>
                      <Label className="label-overline">How Did You Find Us? *</Label>
                      <select
                        value={formData.how_found_us}
                        onChange={(e) => setFormData({ ...formData, how_found_us: e.target.value })}
                        className="w-full mt-2 input-form"
                        required
                      >
                        <option value="">Select...</option>
                        {referralSources.map((source) => (
                          <option key={source.id} value={source.id}>{source.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">Additional Information</Label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={4} className="mt-2" placeholder="Tell us about your business..." />
                  </div>
                  <Button type="submit" disabled={loading} className="btn-primary w-full">
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
